/* eslint-disable react/prop-types */
import React, { FC, useEffect, useState } from 'react';
import styles from './FilterDropdown.module.scss';
import { PropertyType } from '../../../../../common';
import { Checkbox, Search } from 'esa-ui-library';
import { cloneDeep, filter, findIndex, some } from 'lodash';

interface FilterDropdownProps {
	data: PropertyType[];
	onChange: (value: PropertyType) => void;
	isSearchable?: boolean;
	enableSelectAll?: boolean;
	onSelectAll?: () => void;
	selectAllLabel?: string;
}

const FilterDropdown: FC<FilterDropdownProps> = React.memo(function FilterDropdown({
	data,
	onChange,
	isSearchable,
	enableSelectAll,
	onSelectAll,
	selectAllLabel,
}): JSX.Element {
	const [shouldReset, setShouldReset] = useState(false);
	const [searchList, setSearchList] = useState<PropertyType[]>(cloneDeep(data));

	const renderList = (list: PropertyType[]) =>
		list.map((s: PropertyType) => (
			<Checkbox
				key={s.value}
				label={s.label}
				checked={s.isChecked}
				className={styles.Option}
				onChange={() => {
					onChange(s);
					setShouldReset(true);
				}}
			/>
		));

	const filterSearched = (list: PropertyType[]) => {
		const newData = list.filter((item) => findIndex(searchList, ['value', item.value]) !== -1);
		setSearchList(newData);
	};

	useEffect(() => {
		filterSearched(data);
	}, [JSON.stringify(data)]);

	return (
		<section data-testid='filter-dropdown' className={styles.FilterDropdown} onClick={(e) => e.stopPropagation()}>
			{isSearchable && (
				<div className={styles.SearchContainer}>
					<Search
						data={data}
						shouldReset={shouldReset}
						setShouldReset={setShouldReset}
						autoFocus
						searchProperty='label'
						onSearch={setSearchList}
						small
					/>
				</div>
			)}
			<div className={isSearchable ? styles.List : ''}>
				{enableSelectAll && (
					<div
						className={styles.Option}
						onClick={() => {
							onSelectAll && onSelectAll();
							setShouldReset(true);
						}}
						data-testid='select-all-option'
					>
						{selectAllLabel || 'Select All'}
					</div>
				)}
				{some(searchList, 'isChecked') && (
					<div className={styles.SelectedList}>{renderList(filter(searchList, 'isChecked'))}</div>
				)}
				{renderList(filter(searchList, ['isChecked', false]))}
				{searchList.length === 0 && <p className={styles.EmptyList}>No results</p>}
			</div>
		</section>
	);
});

export { FilterDropdown };
